// SPDX-License-Identifier: AGPL-3.0-or-later

import {Logger} from '@app/features/platform/utils/AppLogger';
import {
	getGeneratorVideoCtor,
	getVideoFrameCtor,
	MAX_GENERATOR_PENDING_VIDEO_FRAMES,
	type RendererVideoFrame,
} from '@app/features/voice/utils/native_screen_capture_bridge/shared';

const logger = new Logger('InboundVideoBridge');

export function i420VideoFrameLayout(width: number, height: number): Array<{offset: number; stride: number}> {
	const chromaWidth = width >> 1;
	const yPlaneBytes = width * height;
	const uPlaneBytes = chromaWidth * (height >> 1);
	return [
		{offset: 0, stride: width},
		{offset: yPlaneBytes, stride: chromaWidth},
		{offset: yPlaneBytes + uPlaneBytes, stride: chromaWidth},
	];
}

export function tightI420ByteLength(width: number, height: number): number {
	const chromaWidth = width >> 1;
	const chromaHeight = height >> 1;
	return width * height + 2 * chromaWidth * chromaHeight;
}

export interface InboundVideoFrameInput {
	width: number;
	height: number;
	timestampUs: number;
	data: ArrayBuffer;
}

export interface InboundVideoBridgeHandle {
	track: MediaStreamTrack;
	stream: MediaStream;
	pushFrame: (frame: InboundVideoFrameInput) => void;
	cleanup: () => Promise<void>;
}

function isValidInboundFrame(frame: InboundVideoFrameInput): boolean {
	if (!Number.isSafeInteger(frame.width) || frame.width <= 0 || frame.width % 2 !== 0) return false;
	if (!Number.isSafeInteger(frame.height) || frame.height <= 0 || frame.height % 2 !== 0) return false;
	if (!(frame.data instanceof ArrayBuffer)) return false;
	return frame.data.byteLength >= tightI420ByteLength(frame.width, frame.height);
}

export function createInboundVideoBridge(diagnosticKey: string): InboundVideoBridgeHandle | null {
	const Generator = getGeneratorVideoCtor();
	const VideoFrameImpl = getVideoFrameCtor();
	if (!Generator || !VideoFrameImpl) {
		logger.warn('MediaStreamTrackGenerator path unavailable for inbound native video', {diagnosticKey});
		return null;
	}
	const generator = new Generator({kind: 'video'});
	const writer = generator.writable.getWriter();
	const stream = new MediaStream([generator]);
	let cleanedUp = false;
	let activeWrite: Promise<void> | null = null;
	const pendingFrames: Array<RendererVideoFrame> = [];
	const closeFrame = (frame: RendererVideoFrame): void => {
		try {
			frame.close();
		} catch {}
	};
	const pumpPendingFrames = (): void => {
		if (cleanedUp || activeWrite || pendingFrames.length === 0) return;
		const videoFrame = pendingFrames.shift()!;
		try {
			activeWrite = writer
				.write(videoFrame)
				.catch((error) => {
					logger.warn('Failed to write inbound video frame to generator', {diagnosticKey, error});
				})
				.finally(() => {
					closeFrame(videoFrame);
					activeWrite = null;
					pumpPendingFrames();
				});
		} catch (error) {
			logger.warn('Failed to write inbound video frame to generator', {diagnosticKey, error});
			closeFrame(videoFrame);
			activeWrite = null;
			pumpPendingFrames();
		}
	};
	const enqueueVideoFrame = (videoFrame: RendererVideoFrame): void => {
		if (cleanedUp) {
			closeFrame(videoFrame);
			return;
		}
		pendingFrames.push(videoFrame);
		while (pendingFrames.length > MAX_GENERATOR_PENDING_VIDEO_FRAMES) {
			const dropped = pendingFrames.shift();
			if (dropped) closeFrame(dropped);
		}
		pumpPendingFrames();
	};
	const buildVideoFrame = (frame: InboundVideoFrameInput): RendererVideoFrame | null => {
		try {
			const view = new Uint8Array(frame.data);
			const timestamp = Math.max(0, Math.round(frame.timestampUs));
			return new VideoFrameImpl(view, {
				format: 'I420',
				codedWidth: frame.width,
				codedHeight: frame.height,
				displayWidth: frame.width,
				displayHeight: frame.height,
				timestamp,
				layout: i420VideoFrameLayout(frame.width, frame.height),
			});
		} catch (error) {
			logger.warn('Failed to construct inbound I420 VideoFrame', {diagnosticKey, error});
			return null;
		}
	};
	const pushFrame = (frame: InboundVideoFrameInput): void => {
		if (cleanedUp) return;
		if (!isValidInboundFrame(frame)) {
			logger.debug('Dropping invalid inbound video frame', {
				diagnosticKey,
				width: frame.width,
				height: frame.height,
				byteLength: frame.data?.byteLength,
			});
			return;
		}
		const videoFrame = buildVideoFrame(frame);
		if (videoFrame) {
			enqueueVideoFrame(videoFrame);
		}
	};
	const cleanup = async (): Promise<void> => {
		if (cleanedUp) return;
		cleanedUp = true;
		for (const videoFrame of pendingFrames.splice(0)) {
			closeFrame(videoFrame);
		}
		if (activeWrite) {
			await activeWrite;
		}
		try {
			await writer.close();
		} catch {}
		try {
			generator.stop();
		} catch {}
	};
	return {track: generator, stream, pushFrame, cleanup};
}
